import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { toast } from "sonner";

type FounderCheckResponse = { isFounder?: boolean };

/**
 * /auth/callback
 * ----------------------------------------------------------------
 * Handles the redirect target for magic-link sign-in, email
 * confirmation, and OAuth flows.
 *
 * Supabase JS auto-detects tokens in the URL hash on page load and
 * fires onAuthStateChange — we just need to wait for the session to
 * land and route the user to the right place.
 */
const AuthCallback = () => {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [errored, setErrored] = useState(false);

  // Surface explicit error params from Supabase (expired link, etc.).
  useEffect(() => {
    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const err = params.get("error_description") || hashParams.get("error_description");
    if (err) {
      toast.error(err.replace(/\+/g, " "));
      setErrored(true);
    }
  }, [params]);

  // Once a session lands (or we confirm there is none), route accordingly.
  useEffect(() => {
    if (loading) return;
    if (errored) {
      navigate("/auth", { replace: true });
      return;
    }
    if (session) {
      const next = params.get("next") || "/";
      // Onboarding gate: founders bypass onboarding entirely.
      void Promise.all([
        supabase.functions.invoke<FounderCheckResponse>("founder-check"),
        supabase
          .from("profiles")
          .select("onboarding_completed_at")
          .eq("id", session.user.id)
          .maybeSingle(),
      ]).then(([founderRes, profileRes]) => {
          if (founderRes.data?.isFounder || profileRes.data?.onboarding_completed_at) {
            navigate(next, { replace: true });
          } else {
            navigate("/onboarding", { replace: true });
          }
        });
    }
  }, [loading, session, errored, navigate, params]);

  // Failsafe: if we've been here >8s with no session and no error, bail.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!session && !errored) {
        toast.error("Sign-in link could not be verified. Please try again.");
        navigate("/auth", { replace: true });
      }
    }, 8000);
    return () => clearTimeout(t);
  }, [session, errored, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
      <div className="text-center space-y-3">
        <div className="mx-auto h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <p className="text-sm text-muted-foreground">Signing you in…</p>
      </div>
    </div>
  );
};

export default AuthCallback;
